import * as THREE from 'three';

export interface SpawnOptions {
    pos: THREE.Vector3;
    vel: THREE.Vector3;
    /** Radians per second around each axis. */
    ang: THREE.Vector3;
    scale: number;
    color: THREE.Color;
    life: number;
    gravity: number;
    /** 0..1 velocity kept per second. */
    drag: number;
    /** Sideways sway, for paper that flutters down. */
    flutter?: number;
}

/** A pool of rigid pieces (tomato chunks, seeds, confetti) drawn as one instanced mesh. */
export class Debris {
    readonly mesh: THREE.InstancedMesh;
    private readonly n: number;
    private cursor = 0;
    private readonly pos: Float32Array;
    private readonly vel: Float32Array;
    private readonly rot: Float32Array;
    private readonly ang: Float32Array;
    private readonly scale: Float32Array;
    private readonly life: Float32Array;
    private readonly maxLife: Float32Array;
    private readonly grav: Float32Array;
    private readonly drag: Float32Array;
    private readonly flutter: Float32Array;
    private readonly phase: Float32Array;
    private readonly dummy = new THREE.Object3D();
    private alive = 0;

    constructor(
        geometry: THREE.BufferGeometry,
        material: THREE.Material,
        count: number
    ) {
        this.n = count;
        this.mesh = new THREE.InstancedMesh(geometry, material, count);
        this.mesh.frustumCulled = false;
        this.mesh.instanceColor = new THREE.InstancedBufferAttribute(
            new Float32Array(count * 3),
            3
        );
        this.pos = new Float32Array(count * 3);
        this.vel = new Float32Array(count * 3);
        this.rot = new Float32Array(count * 3);
        this.ang = new Float32Array(count * 3);
        this.scale = new Float32Array(count);
        this.life = new Float32Array(count);
        this.maxLife = new Float32Array(count);
        this.grav = new Float32Array(count);
        this.drag = new Float32Array(count);
        this.flutter = new Float32Array(count);
        this.phase = new Float32Array(count);
        const zero = new THREE.Matrix4().makeScale(0, 0, 0);
        for (let i = 0; i < count; i++) this.mesh.setMatrixAt(i, zero);
    }

    spawn(o: SpawnOptions) {
        const i = this.cursor;
        this.cursor = (this.cursor + 1) % this.n;
        if (this.life[i] <= 0) this.alive += 1;
        o.pos.toArray(this.pos, i * 3);
        o.vel.toArray(this.vel, i * 3);
        o.ang.toArray(this.ang, i * 3);
        this.rot[i * 3] = Math.random() * 6.28;
        this.rot[i * 3 + 1] = Math.random() * 6.28;
        this.rot[i * 3 + 2] = Math.random() * 6.28;
        this.scale[i] = o.scale;
        this.life[i] = o.life;
        this.maxLife[i] = o.life;
        this.grav[i] = o.gravity;
        this.drag[i] = o.drag;
        this.flutter[i] = o.flutter ?? 0;
        this.phase[i] = Math.random() * 6.28;
        this.mesh.setColorAt(i, o.color);
        this.mesh.instanceColor!.needsUpdate = true;
    }

    update(dt: number, time: number) {
        if (this.alive <= 0) return;
        const d = this.dummy;
        for (let i = 0; i < this.n; i++) {
            if (this.life[i] <= 0) continue;
            this.life[i] -= dt;
            if (this.life[i] <= 0) {
                this.alive -= 1;
                d.scale.setScalar(0);
                d.updateMatrix();
                this.mesh.setMatrixAt(i, d.matrix);
                continue;
            }
            const k = Math.pow(this.drag[i], dt);
            this.vel[i * 3] *= k;
            this.vel[i * 3 + 1] = this.vel[i * 3 + 1] * k + this.grav[i] * dt;
            this.vel[i * 3 + 2] *= k;
            this.pos[i * 3] += this.vel[i * 3] * dt;
            this.pos[i * 3 + 1] += this.vel[i * 3 + 1] * dt;
            this.pos[i * 3 + 2] += this.vel[i * 3 + 2] * dt;
            if (this.flutter[i])
                this.pos[i * 3] +=
                    Math.sin(time * 6 + this.phase[i]) * this.flutter[i] * dt;
            this.rot[i * 3] += this.ang[i * 3] * dt;
            this.rot[i * 3 + 1] += this.ang[i * 3 + 1] * dt;
            this.rot[i * 3 + 2] += this.ang[i * 3 + 2] * dt;
            d.position.set(
                this.pos[i * 3],
                this.pos[i * 3 + 1],
                this.pos[i * 3 + 2]
            );
            d.rotation.set(
                this.rot[i * 3],
                this.rot[i * 3 + 1],
                this.rot[i * 3 + 2]
            );
            d.scale.setScalar(this.scale[i] * Math.min(1, this.life[i] / 0.4));
            d.updateMatrix();
            this.mesh.setMatrixAt(i, d.matrix);
        }
        this.mesh.instanceMatrix.needsUpdate = true;
    }
}
